import ApiService from '@shared/services/api.service';

const crawlEndpoint = '/firecrawl/crawl';
const crawlsEndpoint = '/firecrawl/crawls';
const schedulesEndpoint = '/firecrawl/schedules';

/**
 * Normalizes trailing `+00:00` to `Z` for ISO date strings (client display/parsing consistency).
 * @param {unknown} value
 * @returns {unknown}
 */
function normalizeDate(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/\+00:00$/, 'Z');
}

/**
 * Shallow-clones `obj` and normalizes listed date fields with {@link normalizeDate}.
 * @param {object|null|undefined} obj
 * @param {string[]} keys
 * @returns {object|null|undefined}
 */
function normalizeDates(obj, keys) {
  if (!obj) return obj;
  const result = { ...obj };
  keys.forEach((key) => {
    if (result[key]) result[key] = normalizeDate(result[key]);
  });
  return result;
}

/** Date keys applied to crawl rows in {@link WebCrawlService.getCrawls} / {@link WebCrawlService.getCrawl}. */
const dateFields = ['created_at', 'completed_at'];

/**
 * HTTP client for Website Crawl (`/firecrawl/*`). Responses are the JSON body from `ApiService` (Axios `response.data`).
 * @namespace
 */
const WebCrawlService = {
  /**
   * `POST /firecrawl/crawl` — start a crawl.
   * Body: `{ domains: string[], max_depth?: 1–20, depth_mode?: 'path'|'click',
   * limit?: 1–10000, url_report_mode?: 'new'|'all', contact_group_id?: number|null }`.
   * @param {Record<string, *>} data — crawl request payload.
   * @param {object} [options]
   * @param {string} [options.idempotencyKey] — sent as `Idempotency-Key` header (reuse on retry of the same submit).
   * @returns {Promise<*>} Resolved API payload from `ApiService.post`.
   */
  submitCrawl: async (data, options = {}) => {
    const config = {};
    if (options.idempotencyKey) {
      config.headers = { 'Idempotency-Key': options.idempotencyKey };
    }
    return ApiService.post(crawlEndpoint, data, config);
  },
  /**
   * `GET /firecrawl/crawls` — paginated crawl list with optional search.
   * Query: `page`, `per_page` (default 20, max 100), optional `search` (empty = no filter).
   * `meta.total` is the filtered row count for pagination.
   * Mutates `response.data` rows in place to normalize `created_at` / `completed_at`.
   * @param {object} [params]
   * @param {number|string} [params.page] — 1-based; default 1.
   * @param {number|string} [params.perPage] — mapped to `per_page`; default 20, clamped 1–100.
   * @param {string} [params.search] — trimmed; omitted from query if empty.
   * @returns {Promise<{ data?: object[], meta?: object } & Record<string, *>>} API list payload.
   */
  getCrawls: async (params = {}) => {
    const page = Math.max(1, parseInt(params.page, 10) || 1);
    let perPage = parseInt(params.perPage, 10);
    if (!Number.isFinite(perPage) || perPage < 1) perPage = 20;
    perPage = Math.min(100, Math.max(1, perPage));
    const qs = new URLSearchParams({
      page: String(page),
      per_page: String(perPage),
    });
    const search = params.search != null ? String(params.search).trim() : '';
    if (search) {
      qs.append('search', search);
    }
    const response = await ApiService.get(`${crawlsEndpoint}?${qs.toString()}`);
    if (response && response.data && Array.isArray(response.data)) {
      response.data = response.data.map((item) => normalizeDates(item, dateFields));
    }
    return response;
  },
  /**
   * `GET /firecrawl/crawls/:id` — single crawl detail.
   * @param {string|number} id — crawl id.
   * @returns {Promise<object>} Crawl object with `created_at` / `completed_at` normalized.
   */
  getCrawl: async (id) => {
    const response = await ApiService.get(`${crawlsEndpoint}/${id}`);
    return normalizeDates(response, dateFields);
  },
  /**
   * `POST /firecrawl/crawls/:id/cancel` — cancel a pending or processing crawl.
   * Returns 200 `{ success: true }` on success.
   * Returns 409 if the crawl is not in a cancellable state.
   * @param {string|number} id
   * @returns {Promise<*>}
   */
  cancelCrawl: async (id) => ApiService.post(`${crawlsEndpoint}/${id}/cancel`, {}),
  /**
   * `DELETE /firecrawl/crawls/:id` — removes the crawl record (and related data per server rules).
   * @param {string|number} id
   * @returns {Promise<*>}
   */
  deleteCrawl: async (id) => ApiService.delete(`${crawlsEndpoint}/${id}`),
  /**
   * `GET /firecrawl/crawls/:id/results` — all crawl job results (not paginated).
   * Response: `{ crawl_request_id, depth_mode, url_report_mode, data: Job[], total: number }`.
   * Each job includes `urls`, `url_depths`, `unresolved_depth_urls`, and `urls_by_level`.
   * @param {string|number} id
   * @returns {Promise<*>}
   */
  getCrawlResults: async (id) => ApiService.get(`${crawlsEndpoint}/${id}/results`),
  /**
   * `POST /firecrawl/schedules` — create a schedule (same validation rules as {@link WebCrawlService.updateSchedule}).
   * Server expects UTC: `time` (HH:mm), `day_of_week` (0–6) when weekly, `one_time_date` (ISO) when once,
   * `frequency` `once` | `daily` | `weekly`, `depth_mode` `path` | `click`, and
   * `url_report_mode` `new` | `all`. Required: `domains`, `frequency`; conditional: `day_of_week` / `one_time_date`.
   * @param {Record<string, *>} data — full schedule payload.
   * @returns {Promise<*>}
   */
  createSchedule: async (data) => ApiService.post(schedulesEndpoint, data),
  /**
   * `GET /firecrawl/schedules` — list schedules.
   * Mutates each row’s date fields (`next_run_at`, `last_run_at`, `one_time_date`, `created_at`, `completed_at`) for display.
   * @returns {Promise<{ data?: object[] } & Record<string, *>>}
   */
  getSchedules: async () => {
    const response = await ApiService.get(schedulesEndpoint);
    if (response && response.data) {
      response.data = response.data.map((item) =>
        normalizeDates(item, ['next_run_at', 'last_run_at', 'one_time_date', ...dateFields])
      );
    }
    return response;
  },
  /**
   * `PUT /firecrawl/schedules/:id` — replace a schedule (no partial updates). Send the full payload;
   * including `depth_mode` and `url_report_mode`; `next_run_at` is recalculated on the server.
   * @param {string|number} id
   * @param {Record<string, *>} data — same shape as {@link WebCrawlService.createSchedule}.
   * @returns {Promise<*>}
   */
  updateSchedule: async (id, data) => ApiService.put(`${schedulesEndpoint}/${id}`, data),
  /**
   * `DELETE /firecrawl/schedules/:id`
   * @param {string|number} id
   * @returns {Promise<*>}
   */
  deleteSchedule: async (id) => ApiService.delete(`${schedulesEndpoint}/${id}`),
};

export default WebCrawlService;
